import apiFetch from '@wordpress/api-fetch';
import DOMPurify from 'dompurify';
import { getSpinner } from '@astra-utils/helpers';

/**
 * Returns the appropriate action string based on the given status.
 *
 * @param {string} status The status of the plugin. Possible values are: 'activated', 'configure', or 'installed'.
 *
 * @return {string} Returns the corresponding action string based on the status.
 */
const getAction = ( status ) => {
	switch ( status ) {
		case 'activated':
			return '';
		case 'configure':
			return 'astra_recommended_plugin_configure';
		case 'installed':
			return 'astra_recommended_plugin_activate';
	}

	return 'astra_recommended_plugin_install';
};

/**
 * Function to trigger plugin action like installation, activation or configuration.
 *
 * @param {HTMLButtonElement} e Button click event.
 */
const triggerAction = ( e ) => {
	e.preventDefault();

	const astraAdmin = window.astra_admin;
	const action = e.target.dataset.action;

	const isButton = e.target.dataset?.type === 'button';

	switch ( action ) {
		case 'astra_recommended_plugin_configure':
			e.target.innerHTML = DOMPurify.sanitize( ( isButton ? getSpinner() : '' ) + astraAdmin.plugin_configuring_text );
			window.location = e.target.dataset.redirection;
			break;

		case 'astra_recommended_plugin_activate':
			activatePlugin( e );
			break;

		case 'astra_recommended_plugin_install':
			const formData = new window.FormData();
			formData.append( 'action', 'astra_recommended_plugin_install' );
			formData.append( '_ajax_nonce', astraAdmin.plugin_installer_nonce );
			formData.append( 'slug', e.target.dataset.slug );

			e.target.innerHTML = DOMPurify.sanitize( ( isButton ? getSpinner() : '' ) + astraAdmin.plugin_installing_text );

			apiFetch( {
				url: astraAdmin.ajax_url,
				method: 'POST',
				body: formData,
			} )
				.then( ( data ) => {
					if ( data.success ) {
						e.target.innerText = astraAdmin.plugin_installed_text;
						activatePlugin( e );
					}
				} )
				.catch( ( error ) => {
					// eslint-disable-next-line no-console
					console.error( 'Error during plugin installation:', error );
				} );
			break;
		default:
			// Do nothing.
			break;
	}
};

/**
 * Function to activate plugin.
 *
 * @param {HTMLButtonElement} e Button click event.
 */
const activatePlugin = ( e ) => {
	const astraAdmin = window.astra_admin;

	const formData = new window.FormData();
	formData.append( 'action', 'astra_recommended_plugin_activate' );
	formData.append( 'security', astraAdmin.plugin_manager_nonce );
	formData.append( 'init', e.target.dataset.init );

	const isButton = e.target.dataset?.type === 'button';

	e.target.innerHTML = DOMPurify.sanitize( ( isButton ? getSpinner() : '' ) + astraAdmin.plugin_activating_text );

	apiFetch( {
		url: astraAdmin.ajax_url,
		method: 'POST',
		body: formData,
	} )
		.then( ( data ) => {
			if ( data.success ) {
				e.target.className =
					'text-[#4AB866] pointer-events-none capitalize text-sm leading-[0.875rem] font-medium rounded-md' +
					( isButton ? ' mt-3 py-[0.5625rem]' : '' );
				e.target.innerText = astraAdmin.plugin_activated_text;
				window.location = e.target.dataset.redirection;
			}
		} )
		.catch( ( error ) => {
			// eslint-disable-next-line no-console
			console.error( 'Error during plugin activation:', error );
		} );
};

export { getAction, getSpinner, triggerAction, activatePlugin };
